import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { api, ApiError } from '@/lib/api-client';
import { useAuth } from '@/lib/auth';
import { formatDateTime } from '@/lib/date-format';
import { useI18n, type TranslationKey } from '@/lib/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownSelect,
  EmptyState,
  ErrorState,
  PageHeader,
  Pagination,
  SkeletonRow,
  Tag,
} from '@/components/ui';

// NOTIF-06: dedicated notifications page — mark-all-read, page-number
// pagination (same convention as the tickets list, see
// ListTicketsQueryDto/TicketsService.findAll), and a type filter. The bell
// dropdown stays as the quick-glance view; this is the full history.
interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}
interface ListResponse {
  data: NotificationItem[];
  unreadCount: number;
  meta: { page: number; limit: number; total: number; totalPages: number };
}

const TYPE_LABEL_KEYS: Record<string, TranslationKey> = {
  ticket_assigned: 'notifications.assigned',
  ticket_group_assigned: 'notifications.groupAssigned',
  group_member_added: 'notifications.groupMemberAdded',
  ticket_reply: 'notifications.reply',
  ticket_note: 'notifications.note',
  sla_warning: 'notifications.slaWarning',
  sla_breach: 'notifications.slaBreach',
  sla_escalation: 'notifications.slaEscalation',
  mention: 'notifications.mention',
};
const TYPE_OPTIONS = Object.keys(TYPE_LABEL_KEYS);
type Translator = ReturnType<typeof useI18n>['t'];

function message(error: unknown, t: Translator) {
  return error instanceof ApiError ? error.message : t('common.requestFailed');
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = React.useState<NotificationItem[]>([]);
  const [meta, setMeta] = React.useState<ListResponse['meta'] | null>(null);
  const [page, setPage] = React.useState(1);
  const [type, setType] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [markingAll, setMarkingAll] = React.useState(false);

  const load = React.useCallback(async (targetPage: number, targetType: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get<ListResponse>('/notifications', {
        params: { page: targetPage, limit: 20, ...(targetType ? { type: targetType } : {}) },
      });
      setItems(response.data);
      setMeta(response.meta);
    } catch (e) {
      setError(message(e, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load(page, type);
  }, [load, page, type]);

  function changeType(nextType: string) {
    setType(nextType);
    setPage(1);
  }

  async function markRead(id: string) {
    await api.put(`/notifications/${id}/read`);
    setItems((previous) => previous.map((item) => (item.id === id ? { ...item, readAt: new Date().toISOString() } : item)));
  }

  async function markAllRead() {
    setMarkingAll(true);
    try {
      await api.put('/notifications/read-all');
      setItems((previous) => previous.map((item) => (item.readAt ? item : { ...item, readAt: new Date().toISOString() })));
    } catch (e) {
      setError(message(e, t));
    } finally {
      setMarkingAll(false);
    }
  }

  function handleItemClick(item: NotificationItem) {
    if (!item.readAt) void markRead(item.id);
    const ticketId = item.data?.ticketId as string | undefined;
    if (ticketId) navigate(`/tickets/${ticketId}`);
  }

  const hasUnread = items.some((item) => !item.readAt);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('notifications.title')}
        description={t('notifications.description')}
        className="mb-0"
        actions={
          <Button variant="outline" onClick={() => void markAllRead()} disabled={markingAll || !hasUnread}>
            <Check aria-hidden="true" />
            {t('notifications.markAllRead')}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('notifications.filter')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-xs">
            <DropdownSelect
              options={[
                { value: '', label: t('notifications.allTypes') },
                ...TYPE_OPTIONS.map((option) => ({ value: option, label: t(TYPE_LABEL_KEYS[option]) })),
              ]}
              value={type}
              onChange={changeType}
              searchable={false}
            />
          </div>
        </CardContent>
      </Card>

      {error && <ErrorState message={error} onRetry={() => void load(page, type)} />}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <SkeletonRow rows={6} />
          ) : items.length === 0 ? (
            <EmptyState
              title={type ? t('notifications.noType') : t('notifications.none')}
              description={
                type
                  ? t('notifications.allTypesHint')
                  : t('notifications.emptyDescription')
              }
            />
          ) : (
            <div>
              {items.map((item) => (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleItemClick(item)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleItemClick(item);
                    }
                  }}
                  className={`flex w-full items-start gap-3 border-b px-4 py-3 text-left last:border-0 hover:bg-surface-hover ${!item.readAt ? 'bg-brand-50/50' : ''}`}
                >
                  <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${!item.readAt ? 'bg-brand-500' : 'bg-transparent'}`} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Tag label={TYPE_LABEL_KEYS[item.type] ? t(TYPE_LABEL_KEYS[item.type]) : item.type} />
                      <p className="truncate text-sm font-medium text-ink">{item.title}</p>
                    </div>
                    {item.body && <p className="mt-0.5 truncate text-xs text-ink-subtle">{item.body}</p>}
                    <p className="mt-0.5 text-[10px] text-ink-subtle">{formatDateTime(item.createdAt, user?.timezone)}</p>
                  </div>
                  {!item.readAt && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void markRead(item.id);
                      }}
                      className="shrink-0 rounded p-1 text-ink-subtle hover:bg-line"
                      title={t('common.markAsRead')}
                      aria-label={t('common.markAsRead')}
                    >
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {meta && meta.totalPages > 1 && (
        <Pagination page={meta.page} pageSize={meta.limit} total={meta.total} onPageChange={setPage} />
      )}
    </div>
  );
}
